import React, { useCallback, useEffect, useRef, useState } from 'react';

import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import Pagination from 'components/ui/pagination';
import { get } from 'helpers/api';

import ColumnHeader from './column_header';
import PageLibraryFilters from './filters';
import PageRow from './page_row';
import type { ColumnConfig, FilterOption, FilterOptions, PageRecord, SortState } from './types';

interface Props {
  apiEndpoint: string;
}

interface IndexResponse {
  records: PageRecord[];
  filterOptions: FilterOptions;
  meta: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Name', sortKey: 'name' },
  { key: 'orientation', label: 'Orientation' },
  { key: 'usedIn', label: 'Used In', sortKey: 'documents_count' },
  { key: 'createdBy', label: 'Created By', sortKey: 'created_by_name' },
  { key: 'lastEdited', label: 'Last Edited', sortKey: 'updated_at' },
];

const pageKey = (page: PageRecord) => `${page.templateType}-${page.id}`;

export default function PageLibrary({ apiEndpoint }: Props) {
  const [pages, setPages] = useState<PageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [pageTypeFilter, setPageTypeFilter] = useState<string[]>([]);
  const [documentFilter, setDocumentFilter] = useState<string[]>([]);
  const [createdByFilter, setCreatedByFilter] = useState<string[]>([]);
  const [orientationFilter, setOrientationFilter] = useState<string[]>([]);
  const [usageFilter, setUsageFilter] = useState<string[]>([]);
  const [sortState, setSortState] = useState<SortState>({ name: 'name', direction: 'asc' });

  const [pageTypeOptions, setPageTypeOptions] = useState<FilterOption[]>([]);
  const [documentOptions, setDocumentOptions] = useState<FilterOption[]>([]);
  const [createdByOptions, setCreatedByOptions] = useState<FilterOption[]>([]);
  const [orientationOptions, setOrientationOptions] = useState<FilterOption[]>([]);

  // Inert for now: selection has no consumers until bulk actions land (BSD-37619).
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const requestSeq = useRef(0);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchText(searchText), 300);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const fetchPages = useCallback(async () => {
    const requestId = ++requestSeq.current;
    setLoading(true);
    try {
      const response: IndexResponse = await get(apiEndpoint, {
        params: {
          page: currentPage,
          search: debouncedSearchText || undefined,
          pageType: pageTypeFilter.length > 0 ? pageTypeFilter : undefined,
          documentId: documentFilter.length > 0 ? documentFilter : undefined,
          createdBy: createdByFilter.length > 0 ? createdByFilter : undefined,
          orientation: orientationFilter.length > 0 ? orientationFilter : undefined,
          usage: usageFilter[0],
          sortBy: sortState.name,
          sortDirection: sortState.direction,
        },
      });
      if (requestId !== requestSeq.current) return;

      setError(false);
      setPages(response.records);
      setTotalCount(response.meta.totalCount);
      setTotalPages(response.meta.totalPages);
      setPageTypeOptions(response.filterOptions.pageTypes);
      setDocumentOptions(response.filterOptions.documents.map(option => (
        { value: String(option.value), label: option.label }
      )));
      setCreatedByOptions(response.filterOptions.createdBy);
      setOrientationOptions(response.filterOptions.orientations);
    } catch {
      if (requestId !== requestSeq.current) return;

      setError(true);
    } finally {
      if (requestId === requestSeq.current) setLoading(false);
    }
  }, [
    apiEndpoint, currentPage, debouncedSearchText, pageTypeFilter, documentFilter,
    createdByFilter, orientationFilter, usageFilter, sortState,
  ]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const resetToFirstPage = () => setCurrentPage(1);

  const handleSort = (sortKey: string, direction: 'asc' | 'desc') => {
    setSortState({ name: sortKey, direction });
    resetToFirstPage();
  };

  const makeFilterHandler = (setter: (values: string[]) => void) => (values: string[]) => {
    setter(values);
    resetToFirstPage();
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    resetToFirstPage();
  };

  const handleClearAllFilters = () => {
    setSearchText('');
    setPageTypeFilter([]);
    setDocumentFilter([]);
    setCreatedByFilter([]);
    setOrientationFilter([]);
    setUsageFilter([]);
    resetToFirstPage();
  };

  const handleSelectedChange = (page: PageRecord, selected: boolean) => {
    const key = pageKey(page);
    setSelectedKeys(current => (selected ? [...current, key] : current.filter(k => k !== key)));
  };

  const allSelected = pages.length > 0 && pages.every(page => selectedKeys.includes(pageKey(page)));

  const handleSelectAll = (checked: boolean) => {
    const pageKeys = pages.map(pageKey);
    setSelectedKeys(current => (
      checked
        ? [...current, ...pageKeys.filter(key => !current.includes(key))]
        : current.filter(key => !pageKeys.includes(key))
    ));
  };

  const hasActiveFilters = debouncedSearchText !== ''
    || pageTypeFilter.length > 0
    || documentFilter.length > 0
    || createdByFilter.length > 0
    || orientationFilter.length > 0
    || usageFilter.length > 0;

  const uploadButton = (
    <Button disabled icon={faPlus} title="Coming soon" variant="primary">
      Upload PDF
    </Button>
  );

  const renderEmptyState = () => {
    if (hasActiveFilters) {
      return (
        <Empty>
          <Empty.Title>No pages match those filters</Empty.Title>
          <Empty.Content>
            <p>Try a different search term or filter, or upload a new page.</p>
            <div className="d-flex justify-content-center gap-2">
              <Button variant="secondary" onClick={handleClearAllFilters}>Clear filters</Button>
              {uploadButton}
            </div>
          </Empty.Content>
        </Empty>
      );
    }

    return (
      <Empty>
        <Empty.Title>Your page library is empty</Empty.Title>
        <Empty.Content>
          <p>Upload a PDF to add your first page.</p>
          {uploadButton}
        </Empty.Content>
      </Empty>
    );
  };

  const renderResults = () => {
    if (error) {
      return (
        <Alert severity="destructive">
          <Alert.Title>Your page library could not be loaded.</Alert.Title>
          <Button variant="secondary" onClick={fetchPages}>Try again</Button>
        </Alert>
      );
    }

    if (pages.length === 0 && totalCount === 0) return renderEmptyState();

    return (
      <>
        <Table className="align-middle table-hover">
          <Table.Header>
            <Table.Row>
              <Table.Head style={{ width: '32px' }}>
                <Checkbox
                  aria-label="Select all pages"
                  checked={allSelected}
                  onCheckedChange={(checked: boolean | 'indeterminate') => handleSelectAll(checked === true)}
                />
              </Table.Head>
              {COLUMNS.map(column => (
                <ColumnHeader
                  columnConfig={column}
                  key={column.key}
                  sortState={sortState}
                  onSort={handleSort}
                />
              ))}
              <Table.Head />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pages.map(page => (
              <PageRow
                key={pageKey(page)}
                page={page}
                selected={selectedKeys.includes(pageKey(page))}
                onSelectedChange={handleSelectedChange}
              />
            ))}
          </Table.Body>
        </Table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={setCurrentPage}
        />
      </>
    );
  };

  if (loading) {
    return (
      <div>
        <h4 className="mb-4">Page Library</h4>
        <div className="mt-2 text-center">
          <div className="spinner-border spinner-xl" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-4">Page Library</h4>

      <PageLibraryFilters
        createdByFilter={createdByFilter}
        createdByOptions={createdByOptions}
        documentFilter={documentFilter}
        documentOptions={documentOptions}
        orientationFilter={orientationFilter}
        orientationOptions={orientationOptions}
        pageTypeFilter={pageTypeFilter}
        pageTypeOptions={pageTypeOptions}
        searchText={searchText}
        usageFilter={usageFilter}
        onCreatedByChange={makeFilterHandler(setCreatedByFilter)}
        onDocumentChange={makeFilterHandler(setDocumentFilter)}
        onOrientationChange={makeFilterHandler(setOrientationFilter)}
        onPageTypeChange={makeFilterHandler(setPageTypeFilter)}
        onSearchChange={handleSearchChange}
        onSearchClear={() => handleSearchChange('')}
        onUsageChange={makeFilterHandler(setUsageFilter)}
      />

      {renderResults()}
    </div>
  );
}
